import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { client } from '../../utils/contentfulClient';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { Helmet } from 'react-helmet-async';

// REPLACE THIS WITH YOUR EXACT BRAND HEX CODE
const BRAND_COLOR = '#2c3e50'; 

function SinglePost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [newerPost, setNewerPost] = useState(null);
  const [olderPost, setOlderPost] = useState(null);
  const [loading, setLoading] = useState(true);

useEffect(() => {
    setLoading(true);
    setNewerPost(null);
    setOlderPost(null);

    // 1. Fetch the current post (Ensure it is allowed on this site)
    client.getEntries({
      content_type: 'blogPost',
      'fields.slug': slug,
      'fields.targetSite[in]': 'Phoenix Object Conservation' // Security check
    })
    .then((response) => {
      const currentPost = response.items[0];
      setPost(currentPost);

      if (currentPost) {
        const createdAt = currentPost.sys.createdAt;
        
        // 2. Fetch Newer (Filtered)
        const fetchNewer = client.getEntries({ 
          content_type: 'blogPost', 
          'sys.createdAt[gt]': createdAt, 
          order: 'sys.createdAt', 
          limit: 1,
          'fields.targetSite[in]': 'Fans & Feathers'
        });
        
        // 3. Fetch Older (Filtered)
        const fetchOlder = client.getEntries({ 
          content_type: 'blogPost', 
          'sys.createdAt[lt]': createdAt, 
          order: '-sys.createdAt', 
          limit: 1,
          'fields.targetSite[in]': 'Fans & Feathers'
        });

        Promise.all([fetchNewer, fetchOlder])
          .then(([newerRes, olderRes]) => {
            setNewerPost(newerRes.items[0] || null);
            setOlderPost(olderRes.items[0] || null);
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    })
    .catch(console.error);
  }, [slug]);

  if (loading) return <p style={{ textAlign: 'center', marginTop: '50px', color: '#555', fontFamily: 'sans-serif' }}>Loading article...</p>;
  if (!post) return <p>Post not found!</p>;

  // Formatted for standard UK dates (e.g., 22 March 2026)
  const formattedDate = new Date(post.sys.createdAt).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const renderOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const { file, title, description } = node.data.target.fields;
        return (
          <img 
            src={`https:${file.url}`} 
            alt={description || title || 'Article image'} 
            style={{ maxWidth: '100%', height: 'auto', margin: '40px auto', display: 'block' }}
          />
        );
      },
    },
  };

  return (
    <article className="single-post-container" style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', color: '#333' }}>
      
      <Helmet>
        <title>{post.fields.title} | Fans & Feathers</title>
        <meta name="description" content={`Read about ${post.fields.title}.`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.fields.title} />
        <meta property="og:description" content={`Read about ${post.fields.title}.`} />
        {post.fields.coverImage && (
          <meta property="og:image" content={`https:${post.fields.coverImage.fields.file.url}`} />
        )}
      </Helmet>

      {/* Back to Blog Link using the Brand Color */}
      <div style={{ marginBottom: '30px' }}>
        <Link to="/blog" style={{ textDecoration: 'none', color: BRAND_COLOR, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
          &larr; Back to Blog
        </Link>
      </div>

      <h2 style={{ fontSize: '2.5rem', fontWeight: '300', marginBottom: '10px', color: '#111', lineHeight: '1.2' }}>
        {post.fields.title}
      </h2>
      
      <p style={{ color: '#777', fontSize: '0.9rem', marginTop: '0', marginBottom: '30px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {formattedDate}
      </p>
      
      {post.fields.coverImage && (
        <img 
          src={`https:${post.fields.coverImage.fields.file.url}`} 
          alt={post.fields.coverImage.fields.title || post.fields.title} 
          className="cover-image"
          style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto 40px auto' }}
        />
      )}
      
      <div className="post-content" style={{ lineHeight: '1.8', fontSize: '1.1rem', color: '#444' }}>
        {documentToReactComponents(post.fields.content, renderOptions)}
      </div> 

{/* Bottom Navigation with Brand Color */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '80px', paddingTop: '30px', borderTop: '1px solid #ddd' }}>
        
        {/* NEWER POST NOW ON THE LEFT */}
        <div style={{ flex: 1, paddingRight: '15px', textAlign: 'left' }}>
          {newerPost && (
            <Link to={`/blog/${newerPost.fields.slug}`} style={{ textDecoration: 'none', color: BRAND_COLOR, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '0.8rem', color: '#777', textTransform: 'uppercase', letterSpacing: '1px' }}>&larr; Newer Post</span>
              <span style={{ fontWeight: '400', marginTop: '8px', fontSize: '1.1rem' }}>{newerPost.fields.title}</span>
            </Link>
          )}
        </div>
        
        {/* OLDER POST NOW ON THE RIGHT */}
        <div style={{ flex: 1, paddingLeft: '15px', textAlign: 'right' }}>
          {olderPost && (
            <Link to={`/blog/${olderPost.fields.slug}`} style={{ textDecoration: 'none', color: BRAND_COLOR, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span style={{ fontSize: '0.8rem', color: '#777', textTransform: 'uppercase', letterSpacing: '1px' }}>Older Post &rarr;</span>
              <span style={{ fontWeight: '400', marginTop: '8px', fontSize: '1.1rem' }}>{olderPost.fields.title}</span>
            </Link>
          )}
        </div>

      </div>
            
    </article>
  );
}

export default SinglePost;